import { Clock, MapPin, MessageCircle, Phone, ShieldCheck, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import QuickAppointment from "@/components/QuickAppointment";
import { SITE, waHref } from "@/data/site";

export default function QuickAppointmentPage() {
  return (
    <>
      <SEO
        title="Hızlı Randevu"
        description="MediEste Estetik & Güzellik'te hızlı randevu talebi bırakın. Ankara Bağlıca / Etimesgut'ta uzman ekibimizle sizi arayarak randevunuzu netleştirelim."
        keywords="hızlı randevu, MediEste randevu, Ankara Bağlıca güzellik merkezi randevu, cilt bakımı randevu, lazer epilasyon randevu"
        path="/hizli-randevu"
      />

      <section className="pt-32 pb-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumb items={[{ label: "Hızlı Randevu" }]} />
      </section>

      <section className="pb-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="chip-green mb-4">Online Randevu</p>
        <h1 className="font-display text-4xl md:text-6xl text-[#0B1B33] leading-[1.05] max-w-3xl mb-5 tracking-tight">
          Randevunuzu birkaç saniyede oluşturun.
        </h1>
        <p className="text-[#4B5A6E] max-w-2xl leading-relaxed text-base md:text-lg">
          Adınızı ve tercih ettiğiniz hizmeti yazın; bilgileriniz WhatsApp mesajı olarak hazırlansın.
          Gönder&apos;e basmanız yeterli — ekibimiz en kısa sürede sizi arayarak randevunuzu netleştirsin.
        </p>
      </section>

      <section className="pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-3">
            <QuickAppointment />
          </div>

          <aside className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl bg-gradient-to-br from-[#A8C520]/10 via-white to-[#3F7A2E]/10 border border-[#DDEBD1] p-6">
              <div className="flex items-center gap-3 mb-3">
                <Sparkles size={20} className="text-[#4F8B2A]" />
                <p className="chip-green">Neden MediEste?</p>
              </div>
              <ul className="space-y-2.5 text-sm text-[#4B5A6E] leading-relaxed">
                <li className="flex gap-2"><ShieldCheck size={16} className="text-[#4F8B2A] shrink-0 mt-0.5" />20 yılı aşkın estetik ve podoloji uzmanlığı.</li>
                <li className="flex gap-2"><ShieldCheck size={16} className="text-[#4F8B2A] shrink-0 mt-0.5" />Steril, hijyen odaklı klinik uygulama.</li>
                <li className="flex gap-2"><ShieldCheck size={16} className="text-[#4F8B2A] shrink-0 mt-0.5" />Kişiye özel bakım planı ve dermokozmetik ürün seçkisi.</li>
                <li className="flex gap-2"><ShieldCheck size={16} className="text-[#4F8B2A] shrink-0 mt-0.5" />Randevu değişiklikleri için esnek çalışma.</li>
              </ul>
            </div>

            <a href={waHref("Merhaba, randevu almak istiyorum.")} target="_blank" rel="noreferrer" className="flex items-start gap-4 p-5 bg-[#10B981] rounded-2xl hover:brightness-110 transition">
              <div className="h-11 w-11 rounded-xl bg-white/20 text-white flex items-center justify-center shrink-0"><MessageCircle size={20} /></div>
              <div>
                <p className="text-[10px] tracking-[0.22em] uppercase text-white/85 mb-1 font-bold">WhatsApp</p>
                <p className="font-display text-lg text-white font-medium leading-tight">Formu doldurmadan direkt yazın</p>
                <p className="text-xs text-white/80 mt-1">Genellikle birkaç dakika içinde yanıt veriyoruz.</p>
              </div>
            </a>

            <a href={`tel:${SITE.phoneRaw}`} className="flex items-start gap-4 p-5 bg-white border border-[#E5EAF0] rounded-2xl hover:border-[#4F8B2A]/40 hover:shadow-md transition">
              <div className="h-11 w-11 rounded-xl bg-[#4F8B2A]/10 text-[#4F8B2A] flex items-center justify-center shrink-0"><Phone size={20} /></div>
              <div>
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#7C8CA0] mb-1 font-bold">Telefon</p>
                <p className="font-display text-lg text-[#0B1B33] font-medium">{SITE.phone}</p>
              </div>
            </a>

            <div className="flex items-start gap-4 p-5 bg-white border border-[#E5EAF0] rounded-2xl">
              <div className="h-11 w-11 rounded-xl bg-[#4F8B2A]/10 text-[#4F8B2A] flex items-center justify-center shrink-0"><Clock size={20} /></div>
              <div>
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#7C8CA0] mb-1 font-bold">Çalışma Saatleri</p>
                <p className="text-sm text-[#0B1B33] leading-snug font-medium">{SITE.hours}</p>
              </div>
            </div>

            <a href={SITE.mapUrl} target="_blank" rel="noreferrer" className="flex items-start gap-4 p-5 bg-white border border-[#E5EAF0] rounded-2xl hover:border-[#4F8B2A]/40 hover:shadow-md transition">
              <div className="h-11 w-11 rounded-xl bg-[#4F8B2A]/10 text-[#4F8B2A] flex items-center justify-center shrink-0"><MapPin size={20} /></div>
              <div>
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#7C8CA0] mb-1 font-bold">Adres</p>
                <p className="text-sm text-[#0B1B33] leading-snug font-medium">{SITE.address}</p>
                <span className="inline-block mt-2 text-[#4F8B2A] text-[11px] tracking-[0.15em] uppercase font-semibold uline-link">Google Haritalar →</span>
              </div>
            </a>
          </aside>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#F5F7FA] border-t border-[#E5EAF0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="chip-green mb-3">Uzman Kadromuz</p>
            <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight leading-tight">
              Ankara Bağlıca&apos;da Deneyimli ve Uzman Ekibimizle Tanışın
            </h2>
            <p className="text-[#4B5A6E] mt-3 text-sm md:text-base leading-relaxed">
              MediEste kliniğimizde podolojiden estetiğe, kalıcı makyajdan lazer epilasyona kadar tüm uygulamalarımız alanında sertifikalı uzman kadromuz tarafından gerçekleştirilmektedir.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                slug: "sevda-karatas",
                name: "Sevda Karataş",
                title: "Uzman Estetisyen — Usta Öğretici",
                image: "/upload/team/sevda-karatas-card.jpg",
                blurb: "Cilt bakımı ve lazer epilasyonda yılların pratiği; annelik özeniyle sıcak hizmet.",
              },
              {
                slug: "serdar-ceylan",
                name: "Serdar Ceylan",
                title: "Podolog — Usta Öğretici",
                image: "/upload/team/serdar-ceylan-card.jpg",
                blurb: "Podoloji, medikal ayak bakımı ve estetik uygulamalarını tek çatı altında buluşturan öncü isim.",
              },
              {
                slug: "selin-salur",
                name: "Selin Salur",
                title: "Master Nail Technician",
                image: "/upload/team/selin-salur.webp",
                blurb: "Modern protez tırnak, Rus manikür teknikleri ve medikal pedikürde uzman dokunuş.",
              },
              {
                slug: "semih-ceylan",
                name: "Semih Ceylan",
                title: "Epilasyon Uzmanı",
                image: "/upload/team/semih-ceylan.webp",
                blurb: "MYK Seviye 4 belgeli, kişiye özel planlama ve seans takibiyle profesyonel erkek lazer epilasyon.",
              },
            ].map((member) => (
              <div
                key={member.slug}
                className="bg-white border border-[#E5EAF0] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group"
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#F5F7FA] relative">
                  <img
                    src={member.image}
                    alt={`${member.name} - ${member.title}`}
                    loading="lazy"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33]/30 via-transparent to-transparent opacity-60"></div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#4F8B2A] mb-1.5">{member.title}</p>
                  <h3 className="font-display text-xl text-[#0B1B33] mb-2">{member.name}</h3>
                  <p className="text-xs text-[#4B5A6E] leading-relaxed mb-4 flex-1">{member.blurb}</p>
                  <a
                    href={`/hakkimizda#${member.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs tracking-[0.14em] uppercase text-[#4F8B2A] font-semibold hover:text-[#3F7A2E] mt-auto"
                  >
                    Uzmanı İncele →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
